import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { getClassNames, formatDate, formatMetric } from '../../../../utils/util';

import Image from '../../image/Image';
import ImageCarousel from '../../image-carousel/ImageCarousel';

import './buyer-listing.scss';

const BuyerListing = ({ listing, actions, mini }) => {
  const {
    status,
    creationDate,
    classification,
    category,
    specification,
    brandName,
    preferredPrice,
    desiredQuantity,
    desiredQuantityMetric,
    preferredBuyingPeriodFrom,
    preferredBuyingPeriodTo,
    preferredSourceLocation,
    destinationLocation,
    linkedMediaExternalReferences,
    additionalInformation,
    currencyCode,
  } = listing;

  const [open, setOpen] = useState(false);
  const [startCloseAnimation, setStartCloseAnimation] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const detailsRef = useRef(null);

  const handleOverviewClick = () => {
    let timeout;

    if (open) {
      setStartCloseAnimation(true);

      timeout = setTimeout(() => setOpen(false), 300);
    } else {
      setOpen(true);
      setStartCloseAnimation(false);
    }

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    if (open && !mini) {
      setTimeout(() => detailsRef.current.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, [open, mini]);

  const renderOverviewContent = () => {
    if (mini) {
      return (
        <>
          <p>{brandName ? brandName : specification.name}</p>
          <p>
            {parseFloat(preferredPrice).toFixed(2)} / {formatMetric(desiredQuantityMetric, 1)}
          </p>
          <p>{formatDate(preferredBuyingPeriodFrom)}</p>
        </>
      );
    }

    return (
      <>
        {linkedMediaExternalReferences && linkedMediaExternalReferences[0] && (
          <Image
            className="buyer-listing__overview__image"
            externalReference={linkedMediaExternalReferences[0]}
            alt={brandName ? brandName : specification.name}
          />
        )}
        {(!linkedMediaExternalReferences || !linkedMediaExternalReferences[0]) && (
          <div className="buyer-listing__no-image">
            <p>No Image </p>
          </div>
        )}
        <div>
          <p>
            <b>{brandName ? brandName : specification.name}</b>
          </p>
          <p>
            {desiredQuantity} {desiredQuantityMetric} @ {currencyCode + ' ' + parseFloat(preferredPrice).toFixed(2)} /{' '}
            {formatMetric(desiredQuantityMetric, 1)}
          </p>
        </div>
      </>
    );
  };

  const renderDetailsContent = () => {
    if (mini) {
      return (
        <div className="buyer-listing__details__grid buyer-listing__details__grid--mini">
          <div className="buyer-listing__details__attribute buyer-listing__details__attribute--mini">
            <p>Desired Quantity:</p>
            <p>
              {desiredQuantity} {desiredQuantityMetric}
            </p>
          </div>
          <div className="buyer-listing__details__attribute buyer-listing__details__attribute--mini">
            <p>Desired Price:</p>
            <p>{currencyCode + ' ' + parseFloat(preferredPrice).toFixed(2)}</p>
          </div>
          <div className="buyer-listing__details__attribute buyer-listing__details__attribute--mini">
            <p>Destination Location:</p>
            <p>{destinationLocation}</p>
          </div>
          <div className="buyer-listing__details__actions buyer-listing__details__actions--mini">{actions}</div>
        </div>
      );
    }

    return (
      <>
        <div className="buyer-listing__details__grid">
          <div className="buyer-listing__details__classification">
            <p>{classification.name}</p>
            <div>
              <p>{category.name}</p>
              <div>
                <p>{specification.name}</p>
                <div>
                  <p>{brandName}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="buyer-listing__details__status">
            <div>
              <p>Status</p>
              <p>{status}</p>
            </div>
            <div>
              <p>Creation Date</p>
              <p>{formatDate(creationDate)}</p>
            </div>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Desired Quantity:</p>
            <p>
              {desiredQuantity} {desiredQuantityMetric}
            </p>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Desired Price:</p>
            <p>{currencyCode + ' ' + parseFloat(preferredPrice).toFixed(2)}</p>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Preferred Buying Period From:</p>
            <p>{formatDate(preferredBuyingPeriodFrom)}</p>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Preferred Buying Period To:</p>
            <p>{formatDate(preferredBuyingPeriodTo)}</p>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Preferred Source Location:</p>
            <p>{preferredSourceLocation}</p>
          </div>

          <div className="buyer-listing__details__attribute">
            <p>Destination Location:</p>
            <p>{destinationLocation}</p>
          </div>
        </div>

        {linkedMediaExternalReferences && linkedMediaExternalReferences.length > 0 && (
          <div className="buyer-listing__details__attribute">
            <p>Photos</p>
            <div className="buyer-listing__details__photos">
              {linkedMediaExternalReferences.map((externalReference, index) => (
                <Image
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className="buyer-listing__details__photos__photo"
                  externalReference={externalReference}
                  alt=""
                />
              ))}
            </div>
          </div>
        )}

        {additionalInformation && (
          <div className="buyer-listing__details__additional-info">
            <p>Additional Information</p>
            <p>{additionalInformation}</p>
          </div>
        )}

        <div className="buyer-listing__details__actions">{actions}</div>
      </>
    );
  };

  return (
    <>
      <section className={getClassNames('buyer-listing', { mini })} ref={detailsRef}>
        <section
          className={getClassNames('buyer-listing__overview', { mini })}
          onClick={handleOverviewClick}
          data-testid="buyer-listing-overview"
        >
          {renderOverviewContent()}
          <span className={getClassNames('buyer-listing__overview__icon-container', { mini })}>
            <div className={getClassNames('buyer-listing__overview__icon', { mini })} />
            <div className={getClassNames('buyer-listing__overview__icon', { mini, open: !startCloseAnimation })} />
          </span>
        </section>

        {open && (
          <section className={getClassNames('buyer-listing__details', { mini, close: startCloseAnimation })}>
            {renderDetailsContent()}
          </section>
        )}
      </section>

      {selectedImageIndex !== undefined && (
        <ImageCarousel
          images={linkedMediaExternalReferences}
          startIndex={selectedImageIndex}
          setStartIndex={setSelectedImageIndex}
        />
      )}
    </>
  );
};

BuyerListing.propTypes = {
  listing: PropTypes.shape({}).isRequired,
  actions: PropTypes.node,
  mini: PropTypes.bool,
};

BuyerListing.defaultProps = {
  actions: null,
  mini: true,
};

export default BuyerListing;
